import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  LinearProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  Send,
  Visibility,
  CheckCircle,
  Schedule,
  AttachMoney,
  AutoAwesome,
  DescriptionOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brand } from '../../theme/colors';
import { useNavigate } from 'react-router-dom';
import DialogCloseButton from '../common/DialogCloseButton';
import AIBidGenerator from './AIBidGenerator';
import AIContractGenerator from '../contracts/AIContractGenerator';
import ContractViewer from '../contracts/ContractViewer';
import contractService from '../../services/contractService';
import { stripMarkdown } from '../documents/documentStyles';

/**
 * Bid List Component
 *
 * Displays a contractor's quotations grouped by the action they need. One
 * status vocabulary — GROUPS — is shared with ContractorBidsPage so the
 * count pills, the work-queue sections, and the filtered views all agree.
 *
 * NOTE: user-facing copy says "quotation"; internal names stay "bid".
 */

// Single grouped status vocabulary. Every bid status lands in exactly one
// non-'all' group, so the counts on the pills and the sections below match.
export const GROUPS = [
  { key: 'all', label: 'All' },
  {
    key: 'action',
    label: 'Needs action',
    statuses: ['draft', 'changes_requested'],
    empty: 'Nothing needs your attention.',
  },
  {
    key: 'with_client',
    label: 'With client',
    statuses: ['submitted', 'under_review'],
    empty: 'No quotations are with clients right now.',
  },
  {
    key: 'accepted',
    label: 'Accepted',
    statuses: ['accepted'],
    empty: 'No accepted quotations yet.',
  },
  {
    key: 'closed',
    label: 'Closed',
    statuses: ['rejected', 'withdrawn'],
    empty: 'No closed quotations.',
  },
];

// Sentence-case a raw status, e.g. 'changes_requested' -> 'Changes requested'.
export const statusLabel = (status) => {
  const label = (status || 'draft').replaceAll('_', ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Count bids per group key. Used by the count pills.
export const groupCounts = (bids = []) => {
  const counts = { all: bids.length };
  GROUPS.forEach((g) => {
    if (g.key === 'all') return;
    counts[g.key] = bids.filter((b) => g.statuses.includes(b.status)).length;
  });
  return counts;
};

// Work-queue sections shown in the 'all' view. draft and changes_requested
// stay as separate sections (both belong to the 'action' group); every other
// status maps through getBidQueueKey so it lands in exactly one section.
const WORK_QUEUE_SECTIONS = [
  { key: 'draft', title: 'Drafts to finish', empty: 'No draft quotations waiting.' },
  { key: 'changes_requested', title: 'Needs revision', empty: 'No quotations need revision.' },
  { key: 'submitted', title: 'With clients', empty: 'No quotations with clients.' },
  { key: 'accepted', title: 'Accepted', empty: 'No accepted quotations yet.' },
  { key: 'closed', title: 'Closed', empty: 'No closed quotations.' },
];

const DURATION_UNIT_LABELS = ['days', 'weeks', 'months'];

const BidList = ({
  projectId,
  projectContext,
  group = 'all',
  bids: bidsProp,
  hideHeader = false,
  onBidChange,
}) => {
  const { getApiClient, user } = useAuth();
  const navigate = useNavigate();

  // Controlled mode: parent (ContractorBidsPage) owns the fetch and passes the
  // bid array down. Uncontrolled mode (projectId call sites) fetches itself.
  const isControlled = Array.isArray(bidsProp);

  const [allBids, setAllBids] = useState(isControlled ? bidsProp : []);
  const [loading, setLoading] = useState(!isControlled);
  const [showGenerator, setShowGenerator] = useState(false);
  const [bidToEdit, setBidToEdit] = useState(null);
  const [selectedBid, setSelectedBid] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bidToDelete, setBidToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Confirm dialog for submitting a quotation to the client (records the
  // contractor's binding assent — the backend signs with their profile name).
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [bidToSubmit, setBidToSubmit] = useState(null);

  // Formal contracts, fetched lazily per accepted bid. { [bidId]: contracts[] }
  const [contractsByBid, setContractsByBid] = useState({});
  const [contractBid, setContractBid] = useState(null);
  const [contractDraft, setContractDraft] = useState(undefined);
  const [showContractGenerator, setShowContractGenerator] = useState(false);
  const [contractViewerOpen, setContractViewerOpen] = useState(false);
  const [activeContract, setActiveContract] = useState(null);
  const [activeContractBid, setActiveContractBid] = useState(null);

  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    if (Array.isArray(bidsProp)) {
      setAllBids(bidsProp);
      setLoading(false);
      return;
    }
    loadBids();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, bidsProp]);

  const loadBids = async () => {
    setLoading(true);
    try {
      const apiClient = getApiClient();
      const endpoint = projectId
        ? `/bids/project/${projectId}`
        : '/bids/contractor/me';

      const response = await apiClient.get(endpoint);
      setAllBids(response.data.data || []);
    } catch (error) {
      console.error('Error loading bids:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh after an action. In controlled mode the parent refetches and hands
  // back a fresh bids prop; uncontrolled mode reloads locally.
  const refreshBids = () => {
    if (!isControlled) loadBids();
    if (onBidChange) onBidChange();
  };

  // Apply the grouped status filter.
  const activeGroup = GROUPS.find((g) => g.key === group) || GROUPS[0];
  const bids = activeGroup.key === 'all'
    ? allBids
    : allBids.filter((bid) => activeGroup.statuses.includes(bid.status));

  const handleMenuOpen = (event, bid) => {
    setAnchorEl(event.currentTarget);
    setSelectedBid(bid);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBid(null);
  };

  // Lazily fetch formal contracts for every accepted bid whenever the list
  // changes. Party-only endpoint, so 403/404 are tolerated silently.
  useEffect(() => {
    const acceptedBids = allBids.filter((b) => b.status === 'accepted');
    if (acceptedBids.length === 0) return;
    acceptedBids.forEach((bid) => {
      loadContractsForBid(bid.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBids]);

  const loadContractsForBid = async (bidId) => {
    try {
      const list = await contractService.listForBid(bidId);
      setContractsByBid((prev) => ({ ...prev, [bidId]: Array.isArray(list) ? list : [] }));
    } catch (error) {
      setContractsByBid((prev) => ({ ...prev, [bidId]: [] }));
    }
  };

  // Menu action opens a confirmation first so submission is deliberate.
  const openSubmitConfirm = (bid) => {
    setBidToSubmit(bid);
    setSubmitConfirmOpen(true);
    handleMenuClose();
  };

  const handleSubmitConfirm = async () => {
    if (!bidToSubmit) return;
    try {
      const apiClient = getApiClient();
      await apiClient.post(`/bids/${bidToSubmit.id}/submit`);
      showMessage('Quotation submitted to the client.', 'success');
      refreshBids();
    } catch (error) {
      console.error('Error submitting bid:', error);
      showMessage(error.response?.data?.message || error.response?.data?.error || 'Failed to submit quotation', 'error');
    } finally {
      setSubmitConfirmOpen(false);
      setBidToSubmit(null);
    }
  };

  const handleDeleteBidClick = (bidId) => {
    setBidToDelete(bidId);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteBidConfirm = async () => {
    if (!bidToDelete) return;

    try {
      const apiClient = getApiClient();
      await apiClient.delete(`/bids/${bidToDelete}`);
      showMessage('Quotation withdrawn.', 'success');
      refreshBids();
    } catch (error) {
      console.error('Error deleting bid:', error);
      showMessage('Failed to withdraw quotation', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setBidToDelete(null);
    }
  };

  const handleEditBid = (bid) => {
    setBidToEdit(bid);
    setShowGenerator(true);
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      submitted: 'info',
      under_review: 'warning',
      accepted: 'success',
      rejected: 'error',
      changes_requested: 'warning',
      withdrawn: 'default',
    };
    return colors[status] || 'default';
  };

  const getBidQueueKey = (bid) => {
    if (['rejected', 'withdrawn'].includes(bid.status)) return 'closed';
    if (bid.status === 'under_review') return 'submitted';
    return bid.status;
  };

  const getPrimaryAction = (bid) => {
    switch (bid.status) {
      case 'draft':
        return {
          label: 'Continue draft',
          icon: <Edit />,
          variant: 'contained',
          handler: () => handleEditBid(bid),
        };
      case 'changes_requested':
        return {
          label: 'Revise quotation',
          icon: <Edit />,
          variant: 'contained',
          color: 'warning',
          handler: () => handleEditBid(bid),
        };
      case 'submitted':
      case 'under_review':
        return {
          label: 'View status',
          icon: <Visibility />,
          variant: 'outlined',
          handler: () => navigate(`/bids/${bid.id}`),
        };
      case 'accepted':
        return {
          label: 'View project',
          icon: <CheckCircle />,
          variant: 'contained',
          color: 'success',
          handler: () => navigate(`/projects/${bid.created_project_id || bid.project_id}`),
        };
      default:
        return {
          label: 'View feedback',
          icon: <Visibility />,
          variant: 'outlined',
          handler: () => navigate(`/bids/${bid.id}`),
        };
    }
  };

  // Secondary "Contract" action for accepted bids, labelled by the latest
  // (non-void) contract's status. Returns null until we know there's something
  // to show / do.
  const getContractAction = (bid) => {
    if (bid.status !== 'accepted') return null;
    const contracts = contractsByBid[bid.id] || [];
    const active = contracts.find((c) => c.status !== 'void') || null;
    const openGenerator = (draft) => {
      setContractBid(bid);
      setContractDraft(draft);
      setShowContractGenerator(true);
    };
    const openViewer = (contract) => {
      setActiveContract(contract);
      setActiveContractBid(bid);
      setContractViewerOpen(true);
    };
    if (!active) {
      return { label: 'Create contract', icon: <DescriptionOutlined />, handler: () => openGenerator(undefined) };
    }
    switch (active.status) {
      case 'draft':
        return { label: 'Resume contract draft', icon: <Edit />, handler: () => openGenerator(active) };
      case 'sent':
        return { label: 'Contract awaiting client', icon: <DescriptionOutlined />, handler: () => openViewer(active) };
      case 'executed':
        return { label: 'View contract', icon: <DescriptionOutlined />, handler: () => openViewer(active) };
      default:
        return { label: 'Create contract', icon: <DescriptionOutlined />, handler: () => openGenerator(undefined) };
    }
  };

  const renderBidCard = (bid) => {
    const action = getPrimaryAction(bid);
    const contractAction = getContractAction(bid);
    const durationUnit = DURATION_UNIT_LABELS.includes(bid.duration_unit) ? bid.duration_unit : 'weeks';

    return (
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2.5,
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:hover': { borderColor: `${brand.primary}88`, boxShadow: 2 },
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 2 }}>
            <Box sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate(`/bids/${bid.id}`)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6">{bid.bid_number}</Typography>
                <Chip
                  label={statusLabel(bid.status)}
                  color={getStatusColor(bid.status)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                {bid.ai_generated && (
                  <Chip
                    label="AI Assisted"
                    size="small"
                    sx={{ bgcolor: brand.primary, color: 'white' }}
                  />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {bid.project_name || 'Untitled Project'}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Cost
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {bid.currency} {parseFloat(bid.total_cost || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body2">
                        {bid.estimated_duration ? `${bid.estimated_duration} ${durationUnit}` : '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {new Date(bid.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {bid.executive_summary && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {stripMarkdown(bid.executive_summary)}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  size="small"
                  variant={action.variant}
                  color={action.color || 'primary'}
                  startIcon={action.icon}
                  onClick={(event) => {
                    event.stopPropagation();
                    action.handler();
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {action.label}
                </Button>
                {contractAction && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={contractAction.icon}
                    onClick={(event) => {
                      event.stopPropagation();
                      contractAction.handler();
                    }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {contractAction.label}
                  </Button>
                )}
              </Box>
              <IconButton onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, bid); }}>
                <MoreVert />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderWorkQueue = () => {
    if (activeGroup.key !== 'all') {
      return (
        <Grid container spacing={2}>
          {bids.map((bid) => (
            <Grid item xs={12} key={bid.id}>
              {renderBidCard(bid)}
            </Grid>
          ))}
        </Grid>
      );
    }

    return WORK_QUEUE_SECTIONS.map((section) => {
      const sectionBids = allBids.filter((bid) => getBidQueueKey(bid) === section.key);
      if (sectionBids.length === 0) return null;

      return (
        <Box key={section.key} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {section.title}
          </Typography>
          <Grid container spacing={2}>
            {sectionBids.map((bid) => (
              <Grid item xs={12} key={bid.id}>
                {renderBidCard(bid)}
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    });
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      {/* Header — suppressed when the parent renders its own single header. */}
      {!hideHeader && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5">Quotation work queue</Typography>
            <Typography variant="body2" color="text.secondary">
              Start quotations from Find Work. Come here to finish drafts, submit completed quotations, and respond to client changes.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AutoAwesome />}
            onClick={() => navigate('/contractor/marketplace')}
            sx={{ background: brand.gradient }}
          >
            Find Work
          </Button>
        </Box>
      )}

      {/* Quotations list */}
      {bids.length === 0 ? (
        <Alert
          severity="info"
          action={
            projectId ? (
              <Button color="inherit" size="small" onClick={() => setShowGenerator(true)}>
                Create quotation
              </Button>
            ) : activeGroup.key === 'all' ? (
              <Button color="inherit" size="small" onClick={() => navigate('/contractor/marketplace')}>
                Find work
              </Button>
            ) : null
          }
        >
          {projectId
            ? 'No quotations for this project yet. Build a professional quotation when the project details are ready.'
            : activeGroup.key === 'all'
              ? 'No quotations yet. Start from Find Work, choose a project, then build a quotation from the project card.'
              : activeGroup.empty}
        </Alert>
      ) : renderWorkQueue()}

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { navigate(`/bids/${selectedBid?.id}`); handleMenuClose(); }}>
          <Visibility sx={{ mr: 1 }} /> View details
        </MenuItem>
        {['draft', 'changes_requested'].includes(selectedBid?.status) && (
          <>
            <MenuItem onClick={() => openSubmitConfirm(selectedBid)}>
              <Send sx={{ mr: 1 }} /> {selectedBid?.status === 'changes_requested' ? 'Resubmit to client' : 'Submit to client'}
            </MenuItem>
            <MenuItem onClick={() => handleEditBid(selectedBid)}>
              <Edit sx={{ mr: 1 }} /> {selectedBid?.status === 'changes_requested' ? 'Revise quotation' : 'Edit'}
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleDeleteBidClick(selectedBid.id)} sx={{ color: 'error.main' }}>
              <Delete sx={{ mr: 1 }} /> Withdraw
            </MenuItem>
          </>
        )}
      </Menu>

      {/* AI Bid Generator Dialog */}
      <AIBidGenerator
        open={showGenerator}
        onClose={() => {
          setShowGenerator(false);
          setBidToEdit(null);
        }}
        projectId={projectId}
        projectContext={{
          ...projectContext,
          // Thread milestone context from bid data when available
          ...(bidToEdit?.milestone_name ? {
            milestoneName: bidToEdit.milestone_name,
            milestoneDescription: bidToEdit.milestone_description || '',
          } : {}),
        }}
        existingBid={bidToEdit}
        onBidCreated={() => {
          setShowGenerator(false);
          setBidToEdit(null);
          refreshBids();
        }}
      />

      {/* Submit Confirmation Dialog */}
      <Dialog
        open={submitConfirmOpen}
        onClose={() => { setSubmitConfirmOpen(false); setBidToSubmit(null); }}
        aria-labelledby="submit-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => { setSubmitConfirmOpen(false); setBidToSubmit(null); }} />
        <DialogTitle id="submit-dialog-title">Submit quotation to client?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The client will be notified in-app and by email. I offer these terms as a binding quotation under
            the Eqaya standard terms, and my typed name will be recorded as my signature.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSubmitConfirmOpen(false); setBidToSubmit(null); }} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSubmitConfirm} variant="contained" startIcon={<Send />} autoFocus>
            Submit quotation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contract creation wizard (contractor) */}
      {showContractGenerator && contractBid && (
        <AIContractGenerator
          open={showContractGenerator}
          onClose={() => { setShowContractGenerator(false); setContractDraft(undefined); }}
          bid={contractBid}
          existingContract={contractDraft}
          onChanged={() => loadContractsForBid(contractBid.id)}
        />
      )}

      {/* Contract review / signing / void */}
      {contractViewerOpen && activeContract && (
        <ContractViewer
          open={contractViewerOpen}
          onClose={() => setContractViewerOpen(false)}
          contract={activeContract}
          bid={activeContractBid}
          user={user}
          onChanged={() => activeContractBid && loadContractsForBid(activeContractBid.id)}
        />
      )}

      {/* Withdraw Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogCloseButton onClick={() => setDeleteDialogOpen(false)} />
        <DialogTitle id="delete-dialog-title">
          Withdraw quotation?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to withdraw this quotation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteBidConfirm} color="error" variant="contained" autoFocus>
            Withdraw
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BidList;
